// Quick actions / FAB shortcuts shown on the dashboard. Up to 4 surfaced
// at once (config schema enforces max 8 stored).

import { useTranslation } from 'react-i18next';
import { Input, Label, Select, SelectTrigger, SelectValue, SelectContent, SelectItem, Button } from '@erp/ui';
import { Trash2, Plus } from '@/components/icons';
import type { Shortcut } from '@erp/app-builder';
import { navigableScreens } from '@erp/app-builder';

interface ShortcutsTabProps {
  value: Shortcut[];
  onChange: (next: Shortcut[]) => void;
}

const ICON_OPTIONS = [
  'plus', 'receipt', 'file-text', 'user-plus', 'scan-barcode', 'shopping-cart',
  'package', 'truck', 'banknote', 'clipboard-list', 'sliders-horizontal',
];

export function ShortcutsTab({ value, onChange }: ShortcutsTabProps) {
  const { t } = useTranslation();
  const screens = navigableScreens();

  function update(idx: number, patch: Partial<Shortcut>) {
    onChange(value.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  }

  function add() {
    if (value.length >= 8) return;
    onChange([
      ...value,
      {
        id: `sc-${Date.now()}`,
        icon: 'plus',
        labelKey: 'shortcuts.custom',
        target: 'sales.invoices.form',
      },
    ]);
  }

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {t('appBuilder.shortcuts.help', {
            defaultValue: 'Boutons d’action rapides affichés sur le tableau de bord (4 max visibles).',
          })}
        </p>
        <Button size="sm" variant="outline" onClick={add} disabled={value.length >= 8}>
          <Plus size={16} />
          {t('appBuilder.shortcuts.add', { defaultValue: 'Ajouter' })}
        </Button>
      </div>

      <div className="space-y-3">
        {value.map((s, idx) => (
          <div key={s.id} className="rounded-lg border p-3 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">#{idx + 1}</span>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => onChange(value.filter((_, i) => i !== idx))}
                className="text-destructive"
              >
                <Trash2 size={16} />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Icône</Label>
                <Select value={s.icon} onValueChange={(v) => update(idx, { icon: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {ICON_OPTIONS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Clé i18n</Label>
                <Input
                  value={s.labelKey}
                  onChange={(e) => update(idx, { labelKey: e.target.value })}
                  placeholder="shortcuts.newInvoice"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Cible</Label>
                <Select value={s.target} onValueChange={(v) => update(idx, { target: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {screens.map((sc) => <SelectItem key={sc.id} value={sc.id}>{sc.id}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
